import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

// Realistic-looking bar heights for the skeleton (simulates real data while loading)
const SKEL_HEIGHTS = [38, 52, 44, 68, 55, 78, 62, 85, 70, 58, 74, 90];

function ChartSkeleton({ tab }) {
  const count = tab === 'daily' ? 14 : 12;
  const heights = Array.from({ length: count }, (_, i) => SKEL_HEIGHTS[i % SKEL_HEIGHTS.length]);

  return (
    <div className="chart-skel" aria-hidden="true">
      <div className="chart-skel-y">
        {[90, 65, 40, 15].map(t => (
          <div key={t} className="chart-skel-ytick" />
        ))}
      </div>

      <div className="chart-skel-plot">
        <div className="chart-skel-bars">
          {heights.map((h, i) => (
            <div key={i} className="chart-skel-col">
              <div className="chart-skel-bar" style={{ height: `${h}%` }} />
            </div>
          ))}
        </div>
        <div className="chart-skel-xlabels">
          {heights.map((_, i) => (
            <div key={i} className="chart-skel-xlabel" />
          ))}
        </div>
      </div>
    </div>
  );
}

function RefreshOverlay({ tab }) {
  return (
    <div className="chart-refresh-overlay">
      <div className="chart-refresh-inner">
        <span className="loader-ring loader-ring--chart" />
        <span className="chart-refresh-text">Loading {tab} data</span>
      </div>
    </div>
  );
}

const formatValue = (value, valuePrefix) =>
  valuePrefix === '$' ? value.toFixed(2) : Math.round(value).toLocaleString('en-US');

const makeTooltip = (valuePrefix) => function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-label">{payload[0].payload.label}</p>
      <p className="chart-tooltip-value">{valuePrefix}{formatValue(payload[0].value, valuePrefix)}</p>
    </div>
  );
};

export default function TrendChart({
  title,
  icon,
  data,
  activeTab,
  onTabChange,
  loading,
  color = 'var(--series-1)',
  valuePrefix = '$',
  showTabs = true,
  emptyText = 'No data for this period',
  height = 260,
}) {
  const hasData = data.length > 0;
  const CustomTooltip = makeTooltip(valuePrefix);

  return (
    <div className="chart-section trend-chart-section">
      <div className="chart-header">
        <div className="chart-title">
          <span className="chart-title-icon" style={{ color }}>{icon}</span>
          {title}
        </div>

        {showTabs && (
          <div className="chart-tabs">
            {['monthly', 'daily'].map(tab => (
              <button
                key={tab}
                className={`chart-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => onTabChange(tab)}
                disabled={loading}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="chart-container">
        {hasData && (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={data} margin={{ top: 22, right: 12, left: 8, bottom: 4 }} barCategoryGap="18%">
              <CartesianGrid stroke="var(--line)" strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="label" stroke="var(--muted)" style={{ fontSize: '11px' }} tickLine={false} axisLine={false} dy={8} />
              <YAxis stroke="var(--muted)" style={{ fontSize: '11px' }} tickFormatter={v => valuePrefix === '$' ? (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`) : v} tickLine={false} axisLine={false} width={58} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: color, fillOpacity: 0.08, radius: 6 }} />
              <Bar dataKey="value" fill={color} radius={[4, 4, 1, 1]} maxBarSize={48} minPointSize={0} />
            </BarChart>
          </ResponsiveContainer>
        )}

        {loading && <RefreshOverlay tab={activeTab} />}

        {!loading && !hasData && (
          <div className="chart-empty">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity=".35">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
            <p>{emptyText}</p>
          </div>
        )}
      </div>
    </div>
  );
}
